import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, User, KeyRound } from "lucide-react";
import { toast } from "sonner";
import { api, adminToken } from "@/lib/api";
import { T } from "@/lib/testIds";

export default function AdminLogin() {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    if (!firstName.trim() || !lastName.trim() || !code.trim()) {
      toast.error("Enter first name, last name, and access code");
      return;
    }
    try {
      setLoading(true);
      const { data } = await api.post("/admin/login", {
        first_name: firstName.trim(),
        last_name: lastName.trim(),
        code: code.trim(),
      });
      adminToken.set(data.token);
      adminToken.setName(data.name);
      toast.success(`Welcome, ${data.name}`);
      navigate("/admin/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Access denied");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center px-4 py-16">
      <div className="w-full max-w-md rounded-3xl bg-white border border-slate-200 shadow-xl p-8 animate-fade-up">
        <div className="w-14 h-14 rounded-2xl bg-[#0A192F] text-emerald-400 flex items-center justify-center mx-auto">
          <ShieldCheck className="w-7 h-7" />
        </div>
        <h1 className="mt-5 text-2xl font-bold text-[#0A192F] text-center">Authorized Access</h1>
        <p className="mt-2 text-sm text-slate-500 text-center">Please identify yourself and enter your access code.</p>
        <form onSubmit={submit} className="mt-6 space-y-4">
          <label className="block">
            <span className="text-sm font-semibold text-slate-700">First Name</span>
            <div className="relative mt-1.5">
              <User className="w-4 h-4 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" />
              <input
                data-testid={T.admin.codeInput}
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                autoFocus
                autoComplete="given-name"
                className="w-full rounded-xl border border-slate-200 pl-11 pr-4 py-3 text-sm focus:border-[#1D4ED8] focus:ring-2 focus:ring-blue-100 outline-none"
              />
            </div>
          </label>
          <label className="block">
            <span className="text-sm font-semibold text-slate-700">Last Name</span>
            <div className="relative mt-1.5">
              <User className="w-4 h-4 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" />
              <input
                data-testid="admin-lastname-input"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                autoComplete="family-name"
                className="w-full rounded-xl border border-slate-200 pl-11 pr-4 py-3 text-sm focus:border-[#1D4ED8] focus:ring-2 focus:ring-blue-100 outline-none"
              />
            </div>
          </label>
          <label className="block">
            <span className="text-sm font-semibold text-slate-700">Admin Access Code</span>
            <div className="relative mt-1.5">
              <KeyRound className="w-4 h-4 text-slate-400 absolute left-4 top-1/2 -translate-y-1/2" />
              <input
                data-testid="admin-code-input-field"
                type="password"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                autoComplete="one-time-code"
                className="w-full rounded-xl border border-slate-200 pl-11 pr-4 py-3 text-sm font-mono tracking-wider focus:border-[#1D4ED8] focus:ring-2 focus:ring-blue-100 outline-none"
              />
            </div>
          </label>
          <button
            type="submit"
            disabled={loading}
            data-testid={T.admin.loginBtn}
            className="w-full inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full bg-[#0A192F] hover:bg-[#12244a] disabled:opacity-60 text-white font-semibold transition-colors"
          >
            {loading ? "Verifying…" : "Continue"}
          </button>
        </form>
        <p className="mt-6 text-xs text-slate-400 text-center">Authorized personnel only. Your name is recorded with every action for audit purposes.</p>
      </div>
    </div>
  );
}
